/**
 * LauncherTile — one place that the empty stage offers: the icon of the place at the top left,
 * its name and description at the bottom left, and a figure under a rule.
 *
 * The tile is transparent, so the dot grid of the stage shows through it. The type, the icon and
 * the padding grow with the tile, so a large tile looks more important.
 */

import type { CSSProperties, ReactElement } from 'react';
import React from 'react';

import { Button } from '@/components/occurra/Button';
import { AttentionDot } from '@/components/occurra/ui/AttentionDot';
import { cn } from '@/lib/utils';
import type { LauncherInfo, LauncherPlace, LauncherSlot } from '@/types/panels';
import { LauncherInfoKind, LauncherTileSize } from '@/types/panels';

import { ActivityFigure } from './ActivityFigure';
import { FleetFigure } from './FleetFigure';
import { HealthFigure } from './HealthFigure';
import { getDescriptionLines, getTileSize } from './launcherLayout';

interface Props {
    place: LauncherPlace;
    slot: LauncherSlot;
    /** What the place holds right now; null for a place that counts nothing. */
    info: LauncherInfo | null;
    hasDot?: boolean;
    onClick: () => void;
}

interface Look {
    box: string;
    icon: string;
    /** The least space between the icon and the title. */
    text: string;
    title: string;
    description: string;
    rule: string;
}

const LOOKS: Record<LauncherTileSize, Look> = {
    [LauncherTileSize.Large]: {
        box: 'p-6',
        icon: 'size-12',
        text: 'pt-2',
        title: 'text-2xl',
        description: 'max-w-md text-sm',
        rule: 'my-5',
    },
    [LauncherTileSize.Medium]: {
        box: 'p-5',
        icon: 'size-9',
        text: 'pt-2',
        title: 'text-lg',
        description: 'text-sm',
        rule: 'my-3.5',
    },
    [LauncherTileSize.Small]: {
        box: 'p-3',
        icon: 'size-6',
        text: 'pt-1',
        title: 'text-sm',
        description: 'text-xs',
        rule: 'my-1.5',
    },
};

/** In the border's color, so the rule reads as part of the tile's frame. */
const RULE = 'h-px shrink-0 bg-occ-border-2';

function getFigure(info: LauncherInfo, size: LauncherTileSize): ReactElement {
    switch (info.kind) {
        case LauncherInfoKind.Fleet:
            return (
                <FleetFigure
                    info={info}
                    size={size}
                />
            );
        case LauncherInfoKind.Health:
            return (
                <HealthFigure
                    info={info}
                    size={size}
                />
            );
        case LauncherInfoKind.Activity:
            return (
                <ActivityFigure
                    info={info}
                    size={size}
                />
            );
        default:
            return info satisfies never;
    }
}

export const LauncherTile = ({
    place, slot, info, hasDot = false, onClick,
}: Props): ReactElement => {
    const Icon = place.icon;
    const size = getTileSize(slot);
    const look = LOOKS[size];
    const style: CSSProperties = {
        gridColumn: `${slot.column} / span ${slot.columnSpan}`,
        gridRow: `${slot.row} / span ${slot.rowSpan}`,
    };
    const lines = getDescriptionLines(size, slot.heightPx, info !== null);
    return (
        <Button
            variant="outlined"
            // The place the tile stands for, as `data-panel-id` names a host: what a test and a
            // click-through address it by, rather than the text inside it.
            data-launcher-place={place.key}
            style={style}
            aria-description={hasDot ? `${place.label} needs your attention` : undefined}
            onClick={onClick}
            className={cn(
                'flex size-full min-w-0 flex-col items-stretch justify-start gap-0',
                'overflow-hidden whitespace-normal rounded-lg text-left font-normal',
                'border-occ-border-2 active:bg-occ-foreground-50',
                look.box,
            )}
        >
            {/* The stroke scales with the icon, so the lucide default of 2 is a 4px line on a
                large tile. */}
            {Icon && (
                <Icon className={cn(
                    'shrink-0 stroke-[1.5] text-occ-foreground-high-contrast',
                    look.icon,
                )}
                />
            )}
            <span className={cn('mt-auto flex min-w-0 flex-col gap-1', look.text)}>
                <span className={cn('flex min-w-0 items-start gap-1', look.title)}>
                    <span className="truncate font-semibold text-occ-foreground-high-contrast">
                        {place.label}
                    </span>
                    {hasDot && <AttentionDot className="mt-[0.3em]" />}
                </span>
                {lines > 0 && (
                    <span className={cn(
                        'text-occ-foreground-secondary',
                        lines === 1 ? 'truncate' : 'line-clamp-2',
                        look.description,
                    )}
                    >
                        {place.description}
                    </span>
                )}
            </span>
            {info !== null && (
                <>
                    <span className={cn(RULE, look.rule)} />
                    {getFigure(info, size)}
                </>
            )}
        </Button>
    );
};
